import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  LogOut, 
  Heart,
  Home,
  ChevronRight,
  Search,
  Download,
  Eye,
  Check,
  X,
  Mail,
  Phone,
  Clock,
  MessageCircle,
  AlertCircle,
  User,
  Calendar,
  MapPin,
  Church,
  FileText,
  UserCheck,
  UserX
} from 'lucide-react';

const AdminInscriptions = () => {
  const { user, logout, isAuthenticated, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('all');
  const [prioriteFilter, setPrioriteFilter] = useState('all');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [loadingActions, setLoadingActions] = useState({});
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showDemandeModal, setShowDemandeModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDemandes();
  }, [isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDemandes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statutFilter !== 'all') params.append('statut', statutFilter);
      if (prioriteFilter !== 'all') params.append('priorite', prioriteFilter);

      const response = await fetch(`http://localhost:8000/api/public/rejoindre?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setDemandes(data.data || []);
      } else {
        console.error('Erreur lors du chargement des demandes');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateDemandeStatut = async (demandeId, statut) => {
    try {
      setLoadingActions(prev => ({ ...prev, [demandeId]: statut }));
      
      const response = await fetch(`http://localhost:8000/api/public/rejoindre/${demandeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ statut })
      });

      if (response.ok) {
        showNotification(`Demande marquée comme ${getStatutLabel(statut)}`, 'success');
        fetchDemandes();
      } else {
        showNotification('Erreur lors de la mise à jour', 'error');
      }
    } catch (error) {
      console.error('Update error:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoadingActions(prev => {
        const newState = { ...prev };
        delete newState[demandeId];
        return newState;
      });
    }
  };

  const deleteDemande = async (demandeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      const response = await fetch(`http://localhost:8000/api/public/rejoindre/${demandeId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showNotification('Demande supprimée avec succès', 'success');
        fetchDemandes();
      } else {
        showNotification('Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Erreur de connexion', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statutFilter !== 'all') params.append('statut', statutFilter);
      if (prioriteFilter !== 'all') params.append('priorite', prioriteFilter);
      
      const response = await fetch(`http://localhost:8000/api/public/rejoindre/export?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Créer un fichier CSV
        const headers = Object.keys(data.data[0] || {});
        const csvContent = [
          headers.join(','),
          ...data.data.map(row => headers.map(header => 
            `"${String(row[header] || '').replace(/"/g, '""')}"`
          ).join(','))
        ].join('\n');
        
        // Télécharger le fichier
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', data.filename.replace('.xlsx', '.csv'));
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
        showNotification('Export réalisé avec succès', 'success');
      } else {
        showNotification('Erreur lors de l\'export', 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Erreur lors de l\'export', 'error');
    }
  };

  const openDemandeModal = (demande) => {
    setSelectedDemande(demande);
    setShowDemandeModal(true);
  };

  const closeDemandeModal = () => {
    setSelectedDemande(null);
    setShowDemandeModal(false);
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'nouvelle': 'Nouvelle',
      'en_cours': 'En cours',
      'acceptee': 'Acceptée',
      'refusee': 'Refusée'
    };
    return labels[statut] || statut;
  };

  const getStatutBadge = (statut) => {
    const styles = {
      'nouvelle': 'bg-blue-100 text-blue-800 border-blue-200',
      'en_cours': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'acceptee': 'bg-green-100 text-green-800 border-green-200',
      'refusee': 'bg-red-100 text-red-800 border-red-200'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${styles[statut]}`}>
        {getStatutLabel(statut)}
      </span>
    );
  };

  const getPrioriteBadge = (priorite) => {
    const styles = {
      'normale': 'bg-blue-100 text-blue-700',
      'haute': 'bg-orange-100 text-orange-700',
      'urgente': 'bg-red-100 text-red-700'
    };

    const labels = {
      'normale': 'Normale', 
      'haute': 'Haute',
      'urgente': 'Urgente'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[priorite]}`}>
        {labels[priorite] || priorite}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchDemandes();
  }, [searchTerm, statutFilter, prioriteFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-purple-600">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Navigation breadcrumb */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-gray-600">
                <Home className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
                <span className="font-medium text-gray-900">Dashboard</span>
                <ChevronRight className="w-4 h-4" />
                <span className="text-purple-600 font-semibold">Demandes d'Inscription</span>
              </div>
            </div>

            {/* User info et actions */}
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-xs text-gray-500 flex items-center">
                  <Heart className="w-3 h-3 mr-1 text-red-500" />
                  Fleurs de la Sainte Famille
                </p>
              </div>
              
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
              >
                <Home className="w-4 h-4 mr-2" />
                Retour au Dashboard
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Titre de la page */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Demandes d'Inscription FSF
          </h1>
          <p className="text-gray-600 text-lg">
            Gérez les demandes d'inscription à la communauté FSF
          </p>
        </div>

        {/* Filtres, recherche et export */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2 lg:mb-0">
              Filtres et recherche
            </h3>
            <button
              onClick={handleExport}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Recherche */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom, email ou ville..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                />
              </div>
            </div>

            {/* Filtre par statut */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={statutFilter}
                onChange={(e) => setStatutFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
              >
                <option value="all">Tous les statuts</option>
                <option value="nouvelle">Nouvelle</option>
                <option value="en_cours">En cours</option>
                <option value="acceptee">Acceptée</option>
                <option value="refusee">Refusée</option>
              </select>
            </div>

            {/* Filtre par priorité */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Priorité
              </label>
              <select
                value={prioriteFilter}
                onChange={(e) => setPrioriteFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
              >
                <option value="all">Toutes les priorités</option>
                <option value="normale">Normale</option>
                <option value="haute">Haute</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des demandes */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-bold text-gray-900">
              Demandes d'inscription ({demandes.length})
            </h3>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des demandes...</p>
            </div>
          ) : demandes.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucune demande trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Couple</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Mariage</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Contact</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Lieu</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Priorité</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Statut</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {demandes.map((demande) => (
                    <tr key={demande.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Heart className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {demande.prenom_epoux} et {demande.prenom_epouse}
                            </p>
                            <p className="text-sm text-gray-500">
                              Famille {demande.nom_epoux}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-2" />
                          {formatDateOnly(demande.date_mariage)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm space-y-1">
                          <div className="flex items-center text-gray-900">
                            <Mail className="w-4 h-4 mr-1" />
                            {demande.email}
                          </div>
                          {demande.telephone && (
                            <div className="flex items-center text-gray-600">
                              <Phone className="w-4 h-4 mr-1" />
                              {demande.telephone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm space-y-1">
                          {demande.ville && (
                            <div className="flex items-center text-gray-900">
                              <MapPin className="w-4 h-4 mr-1" />
                              {demande.ville}
                            </div>
                          )}
                          {demande.paroisse && (
                            <div className="flex items-center text-gray-600">
                              <Church className="w-4 h-4 mr-1" />
                              {demande.paroisse}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getPrioriteBadge(demande.priorite)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatutBadge(demande.statut)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="w-4 h-4 mr-2" />
                          {formatDate(demande.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openDemandeModal(demande)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir les détails"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          {!isReadOnly && demande.statut !== 'acceptee' && (
                            <button
                              onClick={() => updateDemandeStatut(demande.id, 'acceptee')}
                              disabled={loadingActions[demande.id]}
                              className={`p-2 rounded-lg transition-colors ${
                                loadingActions[demande.id] === 'acceptee'
                                  ? 'bg-green-100 text-green-400 cursor-not-allowed'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title="Accepter la demande"
                            >
                              {loadingActions[demande.id] === 'acceptee' ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {!isReadOnly && demande.statut !== 'refusee' && (
                            <button
                              onClick={() => updateDemandeStatut(demande.id, 'refusee')}
                              disabled={loadingActions[demande.id]}
                              className={`p-2 rounded-lg transition-colors ${
                                loadingActions[demande.id] === 'refusee'
                                  ? 'bg-red-100 text-red-400 cursor-not-allowed'
                                  : 'text-red-600 hover:bg-red-50'
                              }`}
                              title="Refuser la demande"
                            >
                              {loadingActions[demande.id] === 'refusee' ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <UserX className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {!isReadOnly && demande.statut !== 'en_cours' && (
                            <button
                              onClick={() => updateDemandeStatut(demande.id, 'en_cours')}
                              disabled={loadingActions[demande.id]}
                              className={`p-2 rounded-lg transition-colors ${
                                loadingActions[demande.id] === 'en_cours'
                                  ? 'bg-yellow-100 text-yellow-400 cursor-not-allowed'
                                  : 'text-yellow-600 hover:bg-yellow-50'
                              }`}
                              title="Marquer en cours"
                            >
                              {loadingActions[demande.id] === 'en_cours' ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {!isReadOnly && (
                            <button
                              onClick={() => deleteDemande(demande.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de détails de la demande */}
        {showDemandeModal && selectedDemande && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header du modal */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center">
                      <Heart className="w-6 h-6 mr-3" />
                      Demande d'Inscription FSF
                    </h2>
                    <p className="text-blue-100 mt-1">
                      Détails complets de la demande
                    </p>
                  </div>
                  <button
                    onClick={closeDemandeModal}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Contenu du modal */}
              <div className="p-6 space-y-6">
                {/* Informations du couple */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-blue-600" />
                    Informations du Couple
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Époux</label>
                      <p className="text-gray-900 font-medium">{selectedDemande.prenom_epoux} {selectedDemande.nom_epoux}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Épouse</label>
                      <p className="text-gray-900 font-medium">{selectedDemande.prenom_epouse} {selectedDemande.nom_epouse}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Date de mariage</label>
                      <p className="text-gray-900 font-medium flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                        {formatDateOnly(selectedDemande.date_mariage)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact et localisation */}
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Mail className="w-5 h-5 mr-2 text-blue-600" />
                    Contact et Localisation
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Email</label>
                      <p className="text-gray-900 font-medium flex items-center">
                        <Mail className="w-4 h-4 mr-2 text-gray-500" />
                        {selectedDemande.email}
                      </p>
                    </div>
                    {selectedDemande.telephone && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Téléphone</label>
                        <p className="text-gray-900 font-medium flex items-center">
                          <Phone className="w-4 h-4 mr-2 text-gray-500" />
                          {selectedDemande.telephone}
                        </p>
                      </div>
                    )}
                    {selectedDemande.ville && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Ville</label>
                        <p className="text-gray-900 font-medium flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                          {selectedDemande.ville}
                        </p>
                      </div>
                    )}
                    {selectedDemande.paroisse && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Paroisse</label>
                        <p className="text-gray-900 font-medium flex items-center">
                          <Church className="w-4 h-4 mr-2 text-gray-500" />
                          {selectedDemande.paroisse}
                        </p>
                      </div>
                    )}
                    {selectedDemande.adresse && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Adresse</label>
                        <p className="text-gray-900 font-medium">{selectedDemande.adresse}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Statut et priorité */}
                <div className="bg-yellow-50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Statut de la Demande</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Statut</label>
                      <div>{getStatutBadge(selectedDemande.statut)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Priorité</label>
                      <div>{getPrioriteBadge(selectedDemande.priorite)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Date de demande</label>
                      <p className="text-gray-900 font-medium flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-gray-500" />
                        {formatDate(selectedDemande.created_at)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Motivation */}
                {selectedDemande.motivation && (
                  <div className="bg-green-50 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <MessageCircle className="w-5 h-5 mr-2 text-green-600" />
                      Motivation du Couple
                    </h3>
                    <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                      <p className="text-gray-900 whitespace-pre-wrap leading-relaxed text-justify">{selectedDemande.motivation}</p>
                    </div>
                  </div>
                )}

                {/* Notes administratives */}
                {selectedDemande.notes_admin && (
                  <div className="bg-orange-50 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
                      Notes Administratives
                    </h3>
                    <div className="bg-white rounded-lg p-4 border-2 border-orange-200">
                      <p className="text-gray-900 whitespace-pre-wrap text-justify">{selectedDemande.notes_admin}</p>
                    </div>
                  </div>
                )}

                {/* Actions rapides */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Actions Rapides</h3>
                  <div className="flex flex-wrap gap-3">
                    {!isReadOnly && selectedDemande.statut !== 'acceptee' && (
                      <button
                        onClick={() => {
                          updateDemandeStatut(selectedDemande.id, 'acceptee');
                          closeDemandeModal();
                        }}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Accepter la demande
                      </button>
                    )}
                    {!isReadOnly && selectedDemande.statut !== 'refusee' && (
                      <button
                        onClick={() => {
                          updateDemandeStatut(selectedDemande.id, 'refusee');
                          closeDemandeModal();
                        }}
                        className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <UserX className="w-4 h-4 mr-2" />
                        Refuser la demande
                      </button>
                    )}
                    {!isReadOnly && selectedDemande.statut !== 'en_cours' && (
                      <button
                        onClick={() => {
                          updateDemandeStatut(selectedDemande.id, 'en_cours');
                          closeDemandeModal();
                        }}
                        className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Marquer en cours
                      </button>
                    )}
                    <a
                      href={`mailto:${selectedDemande.email}?subject=Demande d'inscription FSF`}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Répondre par email
                    </a>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl">
                <div className="flex justify-end">
                  <button
                    onClick={closeDemandeModal}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{notification.message}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInscriptions;